/**
 * 撤销 / 重做 —— 快照式历史，纯逻辑。
 *
 * ## 为什么用快照而不是「命令 + 逆操作」
 *
 * 命令式撤销要给**每一种改动**都写一份逆操作。我们这里改图的路径有十几处
 * （连线、断开、改接、剪线、静音、改宽、改名、删变参实例、拖拽排序、
 *  隐藏端口、粘贴……），每处都写逆操作 == 十几处都可能写漏。
 *
 * 模板图的数据量很小（几十个节点、几百条边），整图快照的代价可以忽略，
 * 换来的是**永远不会漏**。
 *
 * ## 什么时候记
 *
 * 在**改动之前**记一份当前状态（`record`）。撤销时把它换回来。
 * 关键是别在一次拖拽里记几十次 —— 提交点由调用方控制，见 Playground 里的
 * `commit()`。
 */

export interface HistoryEntry<T> {
  /** 这次改动的名字，会显示在菜单上（「撤销 移动节点」） */
  label: string
  state: T
}

export interface History<T> {
  /** 在改动**之前**记一份现状 */
  record(label: string, state: T): void
  /** 撤销；传入当前状态，返回要恢复到的状态 */
  undo(current: T): HistoryEntry<T> | undefined
  /** 重做；传入当前状态，返回要恢复到的状态 */
  redo(current: T): HistoryEntry<T> | undefined
  canUndo(): boolean
  canRedo(): boolean
  /** 下一次撤销 / 重做分别会撤销 / 重做什么（给菜单显示用） */
  peek(): { undo?: string; redo?: string }
  clear(): void
  /** 历史深度（测试用） */
  size(): { past: number; future: number }
}

export interface HistoryOptions {
  /** 最多保留多少步，默认 50 */
  limit?: number
}

export function createHistory<T>(options: HistoryOptions = {}): History<T> {
  const limit = Math.max(1, options.limit ?? 50)

  let past: HistoryEntry<T>[] = []
  let future: HistoryEntry<T>[] = []

  return {
    record(label, state) {
      past.push({ label, state })
      // 超出上限就丢最老的
      if (past.length > limit) past = past.slice(past.length - limit)
      // 产生新分支 → 原来的重做链作废（和所有编辑器一致）
      future = []
    },

    undo(current) {
      const entry = past.pop()
      if (!entry) return undefined
      // 把「当前」推入重做栈，标签沿用同一条 —— 重做要能说清是在重做什么
      future.push({ label: entry.label, state: current })
      return entry
    },

    redo(current) {
      const entry = future.pop()
      if (!entry) return undefined
      past.push({ label: entry.label, state: current })
      return entry
    },

    canUndo: () => past.length > 0,
    canRedo: () => future.length > 0,

    peek() {
      const undoEntry = past[past.length - 1]
      const redoEntry = future[future.length - 1]
      return {
        undo: undoEntry?.label,
        redo: redoEntry?.label,
      }
    },

    clear() {
      past = []
      future = []
    },

    size: () => ({ past: past.length, future: future.length }),
  }
}
